import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import usePageLoading from "@/hooks/usePageLoading";
import StepModal from "./StepModal";
import InvestmentModal from "./InvestmentModal";
import HeaderSection from "./HeaderSection";
import HowItWorksSection from "./HowItWorksSection";
import SectorsSection from "./SectorsSection";
import SuccessMessage from "./SuccessMessage";
import LoadingState from "./LoadingState";

const Investors = () => {
  const { i18n } = useTranslation("common");
  const { addInvestor, seedData } = useStore();
  const currentLang = i18n.language || "ar";

  const [selectedSector, setSelectedSector] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(!seedData);

  usePageLoading(isLoading, 300);

  const sectors = useMemo(() => {
    if (!seedData?.companies) return [];
    return seedData.companies.map((sector) => ({
      id: sector.sector_ar || sector.sector_en,
      sector_ar: sector.sector_ar,
      sector_en: sector.sector_en,
      sector_fr: sector.sector_fr,
      gradient: sector.gradient || "from-blue-500 to-cyan-500",
      companies: sector.companies || [],
    }));
  }, [seedData]);

  const companiesList = useMemo(() => {
    if (!selectedSector) return [];
    const sectorData = sectors.find(
      (s) =>
        s.sector_ar === selectedSector.sector_ar ||
        s.sector_en === selectedSector.sector_en
    );
    if (!sectorData) return [];
    return sectorData.companies
      .map((company) => {
        const name =
          company[
            `${
              currentLang === "ar" ? "ar" : currentLang === "fr" ? "fr" : "en"
            }`
          ] ||
          company.ar ||
          company.en;
        return name;
      })
      .filter(Boolean)
      .sort();
  }, [selectedSector, sectors, currentLang]);

  const handleSectorClick = (sector) => {
    setSelectedSector(sector);
    setIsModalOpen(true);
  };

  const handleStepClick = (step) => {
    setSelectedStep(step);
    setIsStepModalOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    addInvestor(formData);
    setIsModalOpen(false);
    setSubmitted(true);
  };

  if (!seedData) {
    return <LoadingState currentLang={currentLang} />;
  }

  if (submitted) {
    return <SuccessMessage currentLang={currentLang} onExploreMore={() => setSubmitted(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <HeaderSection currentLang={currentLang} />
        
        <HowItWorksSection 
          currentLang={currentLang} 
          onStepClick={handleStepClick}
        />
        
        <SectorsSection 
          sectors={sectors}
          currentLang={currentLang}
          onSectorClick={handleSectorClick}
        />

        <InvestmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedSector={selectedSector}
          onSubmit={handleFormSubmit}
          companiesList={companiesList}
        />

        <StepModal
          isOpen={isStepModalOpen}
          onClose={() => setIsStepModalOpen(false)}
          step={selectedStep}
          currentLang={currentLang}
        />
      </div>
    </div>
  );
};

export default Investors;